import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Heart, Users } from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';

const LoginPage = () => {
  usePageTitle('Staff Sign In');
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.name);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3E2B16] via-[#5C4023] to-[#C87D3E] flex flex-col">
      {/* Full-bleed brand banner — brand name appears exactly once */}
      <div className="w-full bg-[#2E1F10]/60 border-b border-white/10 py-8 sm:py-10 px-6 shadow-[0_8px_30px_rgba(0,0,0,0.25)]" data-testid="login-hero-banner">
        <img
          src="/logo-horizontal-dark.png"
          alt="617Reach — Resources. In Reach. In Boston. For You."
          className="mx-auto h-28 sm:h-36 w-auto"
        />
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">

        {/* Features */}
        <div className="flex justify-center gap-8 mb-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2">
              <Heart className="w-5 h-5 text-[#E9A94F]" />
            </div>
            <span className="text-xs text-[#E8DFD3]/80">Community<br/>Resources</span>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2">
              <Users className="w-5 h-5 text-[#E8DFD3]" />
            </div>
            <span className="text-xs text-[#E8DFD3]/80">Staff<br/>Only</span>
          </div>
        </div>

        {/* Auth Card */}
        <Card className="border-0 shadow-[0_8px_30px_rgba(0,0,0,0.08)] rounded-3xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-center font-['Manrope']">
              Staff Access
            </CardTitle>
            <CardDescription className="text-center">
              Sign in to manage community resources
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#F3EFE9] rounded-full p-1">
                <TabsTrigger 
                  value="login" 
                  data-testid="login-tab"
                  className="rounded-full data-[state=active]:bg-[#5C4023] data-[state=active]:text-white transition-all"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  data-testid="register-tab"
                  className="rounded-full data-[state=active]:bg-[#5C4023] data-[state=active]:text-white transition-all"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="staff@example.org"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="rounded-xl border-[#E0D6C8] focus:ring-[#5C4023]/20 focus:border-[#5C4023]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="rounded-xl border-[#E0D6C8] focus:ring-[#5C4023]/20 focus:border-[#5C4023]"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="login-submit-btn"
                    className="w-full bg-[#5C4023] hover:bg-[#4A331C] text-white rounded-full h-12 font-medium transition-all active:scale-95"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="Jane Smith"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="rounded-xl border-[#E0D6C8] focus:ring-[#5C4023]/20 focus:border-[#5C4023]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="staff@example.org"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="rounded-xl border-[#E0D6C8] focus:ring-[#5C4023]/20 focus:border-[#5C4023]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="Create a password (8+ characters)"
                      minLength={8}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="rounded-xl border-[#E0D6C8] focus:ring-[#5C4023]/20 focus:border-[#5C4023]"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="register-submit-btn"
                    className="w-full bg-[#5C4023] hover:bg-[#4A331C] text-white rounded-full h-12 font-medium transition-all active:scale-95"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#888888] mt-6">
          For authorized staff members only
        </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
